import { useState, useEffect } from 'react';
import { casesApi, clientsApi, parksApi } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { DataTable } from '@/components/DataTable';
import { StatusBadge, PriorityBadge } from '@/components/StatusBadge';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2 } from 'lucide-react';
import { format } from 'date-fns';

const caseTypes = ['inspection', 'claim', 'survey', 'maintenance'];
const caseStatuses = ['open', 'in_progress', 'pending', 'completed', 'closed'];
const priorities = ['low', 'medium', 'high', 'urgent'];

export default function CasesPage() {
  const [cases, setCases] = useState([]);
  const [clients, setClients] = useState([]);
  const [parks, setParks] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingCase, setEditingCase] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    client_id: '',
    park_id: '',
    case_type: 'inspection',
    status: 'open',
    priority: 'medium',
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [casesData, clientsData, parksData] = await Promise.all([
        casesApi.getAll(),
        clientsApi.getAll(),
        parksApi.getAll(),
      ]);
      setCases(casesData);
      setClients(clientsData);
      setParks(parksData);
    } catch (error) {
      toast.error('Failed to load cases');
    } finally {
      setIsLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({ title: '', description: '', client_id: '', park_id: 'none', case_type: 'inspection', status: 'open', priority: 'medium' });
    setEditingCase(null);
  };

  const openDialog = (caseItem = null) => {
    if (caseItem) {
      setEditingCase(caseItem);
      setFormData({
        title: caseItem.title || '',
        description: caseItem.description || '',
        client_id: caseItem.client_id || '',
        park_id: caseItem.park_id || 'none',
        case_type: caseItem.case_type || 'inspection',
        status: caseItem.status || 'open',
        priority: caseItem.priority || 'medium',
      });
    } else {
      resetForm();
    }
    setIsDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      // Convert "none" values to empty strings for backend
      const submitData = {
        ...formData,
        park_id: formData.park_id === 'none' ? '' : formData.park_id
      };
      
      if (editingCase) {
        await casesApi.update(editingCase.id, submitData);
        toast.success('Case updated');
      } else {
        await casesApi.create(submitData);
        toast.success('Case created');
      }
      setIsDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error('Failed to save case');
    }
  };

  const handleDelete = async (caseItem) => {
    if (!window.confirm('Delete this case?')) return;
    try {
      await casesApi.delete(caseItem.id);
      toast.success('Case deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete case');
    }
  };

  const getClientName = (clientId) => {
    const client = clients.find(c => c.id === clientId);
    return client?.name || '-';
  };

  const getParkName = (parkId) => {
    const park = parks.find(p => p.id === parkId);
    return park?.name || '-';
  };

  const columns = [
    { key: 'case_number', label: 'Case #' },
    { key: 'title', label: 'Title' },
    { key: 'client_id', label: 'Client', render: (val) => getClientName(val) },
    { key: 'case_type', label: 'Type', render: (val) => <span className="capitalize">{val}</span> },
    { key: 'priority', label: 'Priority', render: (val) => <PriorityBadge priority={val} /> },
    { key: 'status', label: 'Status', render: (val) => <StatusBadge status={val} /> },
    { key: 'created_at', label: 'Created', render: (val) => val ? format(new Date(val), 'MMM d, yyyy') : '-' },
  ];

  const actions = [
    { label: 'Edit', icon: Pencil, onClick: openDialog },
    { label: 'Delete', icon: Trash2, onClick: handleDelete, destructive: true },
  ];

  return (
    <div className="space-y-6" data-testid="cases-page">
      <div className="page-header">
        <div>
          <h1 className="page-title">Cases</h1>
          <p className="text-sm text-stone-500 mt-1">Manage inspection and claim cases</p>
        </div>
        <Button onClick={() => openDialog()} className="bg-emerald-900 hover:bg-emerald-800" data-testid="add-case-btn">
          <Plus className="h-4 w-4 mr-2" /> Add Case
        </Button>
      </div>

      <DataTable
        columns={columns}
        data={cases}
        actions={actions}
        isLoading={isLoading}
        emptyMessage="No cases found. Create your first case!"
      />

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-lg" data-testid="case-dialog">
          <DialogHeader>
            <DialogTitle>{editingCase ? 'Edit Case' : 'Create New Case'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="title">Title *</Label>
              <Input
                id="title"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                required
                data-testid="case-title-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="client">Client *</Label>
              <Select value={formData.client_id} onValueChange={(val) => setFormData({ ...formData, client_id: val })}>
                <SelectTrigger data-testid="case-client-select">
                  <SelectValue placeholder="Select client" />
                </SelectTrigger>
                <SelectContent>
                  {clients.map((c) => (
                    <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="park">Park/Site</Label>
              <Select value={formData.park_id} onValueChange={(val) => setFormData({ ...formData, park_id: val })}>
                <SelectTrigger data-testid="case-park-select">
                  <SelectValue placeholder="Select park" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  {parks.map((p) => (
                    <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="type">Type</Label>
                <Select value={formData.case_type} onValueChange={(val) => setFormData({ ...formData, case_type: val })}>
                  <SelectTrigger data-testid="case-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {caseTypes.map((t) => (
                      <SelectItem key={t} value={t} className="capitalize">{t}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="priority">Priority</Label>
                <Select value={formData.priority} onValueChange={(val) => setFormData({ ...formData, priority: val })}>
                  <SelectTrigger data-testid="case-priority-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {priorities.map((p) => (
                      <SelectItem key={p} value={p} className="capitalize">{p}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="status">Status</Label>
                <Select value={formData.status} onValueChange={(val) => setFormData({ ...formData, status: val })}>
                  <SelectTrigger data-testid="case-status-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {caseStatuses.map((s) => (
                      <SelectItem key={s} value={s} className="capitalize">{s.replace('_', ' ')}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={3}
                data-testid="case-description-input"
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" className="bg-emerald-900 hover:bg-emerald-800" data-testid="case-submit-btn">
                {editingCase ? 'Update' : 'Create'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
